/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import {
  ACCOUNT_SETTINGS_ROUTE_PERMISSION,
  AccountManagementProtectedRoute,
  AccountManagementProvider,
  AccountSettingsRouter as BaseAccountSettingsRouter,
  PageSkeleton,
} from "@farmlands/account-management";
import { AppError } from "@farmlands/ui-library";
import { FC } from "react";
import { Navigate } from "react-router-dom";

import { PageContainer } from "../../../components/layout/PageContainer";
import { useThemeBackgroundColor } from "../../../hooks/useThemeBackgroundColor";
import { HOME_ROUTE } from "../../../util/routing";
import { useAccountManagementContextValue } from "../hooks/useAccountManagementContextValue";

export const AccountSettingsRouter: FC = () => {
  useThemeBackgroundColor((theme) => theme.colors.shade0);

  const { contextValue, isLoading, error, refetch } = useAccountManagementContextValue();

  if (isLoading) {
    return (
      <PageContainer>
        <PageSkeleton />
      </PageContainer>
    );
  }

  if (error) {
    return <AppError contactUrl={contextValue.config.contactUrl} onTryAgainClick={refetch} />;
  }

  if (!contextValue.account.selectedAccount) {
    return <Navigate to={HOME_ROUTE} replace />;
  }

  return (
    <AccountManagementProvider value={contextValue}>
      <AccountManagementProtectedRoute
        {...ACCOUNT_SETTINGS_ROUTE_PERMISSION}
        redirectTo={HOME_ROUTE}
        loadingFallback={
          <PageContainer>
            <PageSkeleton />
          </PageContainer>
        }
      >
        <PageContainer>
          <BaseAccountSettingsRouter />
        </PageContainer>
      </AccountManagementProtectedRoute>
    </AccountManagementProvider>
  );
};

export default AccountSettingsRouter;
